import { useTranslation } from 'next-i18next'
import { useQuery } from 'react-query'

// Components
import RecommendProductSection from '@/components/shared/RecommendProductSection'

// Libs
import { fetchProductRecommend } from '@/api/fetchProductRecommend'
import useMedia from '@/hooks/useMedia'
import { REC_SOURCE } from '@/methods/recommendTracking'

// Constants
import { GtmItemListName } from '@/constants/gtmEvent'
import { QUERY_KEY } from '@/constants/queryKey'

// 手機 / 平板 3 欄，顯示兩排就好；桌機用後台設定的顯示數量
const MOBILE_RECOMMEND_LIMIT = 6

interface IProps {
  productId: number | string
}

// 相關推薦的標題依後端 source：也買了 / 看了也看 / 你可能也喜歡
const RELATED_TITLE: Record<string, { title: string; itemListName: GtmItemListName }> = {
  also_bought: { title: 'Product_Recommend_AlsoBought', itemListName: GtmItemListName.RECOMMEND_ALSO_BOUGHT },
  also_viewed: { title: 'Product_Recommend_AlsoViewed', itemListName: GtmItemListName.RECOMMEND_ALSO_VIEWED },
  similar: { title: 'Product_Recommend_YouMayLike', itemListName: GtmItemListName.RECOMMEND_SIMILAR },
}

// 商品頁底部的推薦：先相關推薦（後台指定 → 也買了 → 看了也看 → 同類；標題依後端 source），再「熱門商品」（排除上一區已出現的）
const RecommendSection = ({ productId }: IProps) => {
  const { t } = useTranslation()
  const { isTablet, isMobile } = useMedia()
  const limit = isTablet || isMobile ? MOBILE_RECOMMEND_LIMIT : undefined

  const { data: alsoBought, isFetched: alsoBoughtFetched } = useQuery(
    [QUERY_KEY.FETCH_PRODUCT_RECOMMEND, { type: 'also-bought', productId, limit }],
    () => fetchProductRecommend({ type: 'also-bought', productId, limit }),
    { enabled: !!productId, staleTime: 5 * 60 * 1000 },
  )

  const alsoBoughtItems = alsoBought?.items ?? []
  const excludeIds = [productId, ...alsoBoughtItems.map((item) => item.productId)]
  const { data: hot } = useQuery(
    [QUERY_KEY.FETCH_PRODUCT_RECOMMEND, { type: 'hot', excludeIds, limit }],
    () => fetchProductRecommend({ type: 'hot', excludeIds, limit }),
    // 等上一區回來才知道要排除哪些
    { enabled: !!productId && alsoBoughtFetched, staleTime: 5 * 60 * 1000 },
  )
  const hotItems = hot?.items ?? []

  const related = RELATED_TITLE[alsoBought?.source ?? ''] ?? RELATED_TITLE.similar

  return (
    <>
      {alsoBoughtItems.length > 0 && (
        <RecommendProductSection
          title={t(related.title)}
          itemListName={related.itemListName}
          recSource={REC_SOURCE.PRODUCT_RELATED}
          items={alsoBoughtItems}
        />
      )}
      {hotItems.length > 0 && (
        <RecommendProductSection
          title={t('Product_Recommend_Hot')}
          itemListName={GtmItemListName.RECOMMEND_HOT}
          recSource={REC_SOURCE.PRODUCT_HOT}
          items={hotItems}
        />
      )}
    </>
  )
}

export default RecommendSection
